
import {Maintenance, Unauthorized} from 'utils/errors'
import PulseItem from './feed-item.vue'
import $pulse from 'stores/pulse'
import {Later} from 'utils/timers'
import PulseEmpty from '../pulse-empty.vue'
import PulseBottom from '../pulse-bottom.vue'
import FeedFilters from './feed-filters.vue'
import PulseButton from '../pulse-button.vue'
import $tracker from 'stores/tracker'
import {DATES} from '../pulse-defs'

const DAYS_3 = 'last_3_days'

const DAYS_7 = 'last_7_days'

const THRESHOLD = 150

export default {
  components: {
    PulseItem,
    PulseEmpty,
    PulseBottom,
    FeedFilters,
    PulseButton,
  },
  props: {
    chosenId: {
      type: String,
      default: undefined
    }
  },
  emits: ['toggle-tab', 'open-signal', 'update:show_filters'],

  data() {
    return {
      load_new: new Later(30 * 1000),
      filters: {
        search: '',
        favorite: false,
        category: undefined,
        date: DAYS_3,
        entities: [],
      },
      default_date: DAYS_3,
      clearing_filters: false,
      show_filters: true
    }
  },
  computed: {
    tracker_total() {
      return $tracker.total
    },
    items(){
      return $pulse.items
    },
    empty() {
      return this.items.length === 0
    },
    loading() {
      return $pulse.loading || this.clearing_filters
    },
    feed_atend() {
      let count = 0
      const category = this.semantic_search ? 'semantic' : this.filters.category
      if (category) {
        count = this.categories_count[category]
      }
      if (count === 0) count = this.categories_count.all
      return this.items.length >= count
    },
    all_count(){
      if (this.semantic_search) {
        if (!this.categories_count.semantic) return '?'
        return this.feed_atend ? `${this.categories_count.semantic}` : `${this.categories_count.semantic - 1}+`
      } 
      return this.categories_count.all
    },
    more_available() {
      return !this.empty && !this.feed_atend
    },
    categories_count() {
      return $pulse.categories_count
    },
    semantic_search() {
      return this.$global.pulseSemanticSearchEnabled && !!this.filters.search
    },
    has_filters() {
      return !!(
        this.filters.search ||
        this.filters.favorite ||
        this.filters.category ||
        this.filters.date !== this.default_date ||
        this.filters.entities.length
      )
    },
  },
  watch: {
    more_available: {
      handler: async function (val) {
        if (!val) return
        await this.$nextTick()
        this.initBottomObserver()
      },
      immediate: true
    },
    'items.length': function (next, prev){
      if (next >= prev) return
      this.$refs.scroller.scrollToTop(true)
    },
    filters:{
      handler(){
        if (this.clearing_filters) return
        this.$debounce_500(this.loadItems)
      },
      deep: true
    }
  },
  beforeUnmount() {
    this.load_new.abort()
    $pulse.clear()
  },
  async mounted() {
    this.$dispatch('feed:mounted')
    $tracker.load()
    if (this.empty) {
      await this.loadItems()
    }
    if (this.empty) {
      this.filters.date = DAYS_7
      this.default_date = DAYS_7
    }
  },

  methods:{
    openSignal(signal_id){
      this.$emit('open-signal', {signal_id})
    },
    async onWheel(ev) {
      if (this.loading) return
      if (!this.empty) return
      await this.loadItems()
    },
    onScroll(ev) {
      const top = ev.target.scrollTop
      this.show_filters = this.$responsive.mobile && top <= THRESHOLD
    },
    scrollTop(){
      this.$refs.scroller.scrollTop(0, {smooth: true, check_bottom: false})
    },
    async loadItems(params = {}) {
      if (this.semantic_search && !params.more) {
        this.scrollTop()
      }
      const search = this.filters.search
      const filter = []
      const categories = []
      const entities = []
      let date = this.filters.date
      if (date) {
        date = DATES.find(d => d.value === date).date
      }

      if (this.filters.favorite) {
        filter.push('favorite')
      }
      if (this.filters.category) {
        categories.push(this.filters.category)
      }
      if (this.filters.entities) {
        entities.push(...this.filters.entities)
      }
      this.restartTimer()
    
      await $pulse.load({
        search, filter, categories, from: date?.[0], to: date?.[1], entities, semantic: this.semantic_search, ...params
      })
    },
    async onLoadMore() {
      if (this.loading) return
      if (this.empty) return
      if (this.feed_atend) return
      await this.loadItems({more:true})
    },

    async onLoadNew() {
      if (this.loading) return
      if (this.empty) await this.loadItems()
      else await this.loadItems({new: true})
    },

    initBottomObserver(){
      const scroller = this.$refs.scroller
      const observer = this.$refs.scroller.getObserver()
      observer.unobserveAll() 
      observer.observe(
        this.$refs.bottom.getLoader(),
        entry => entry.position === 'visible' && scroller.hasVerticalScroll() && this.onLoadMore()
      )
    },
    restartTimer(){
      this.load_new.abort()
      this.load_new.repeat(async () => {
        try {
          await this.onLoadNew()
        }
        catch(err) {
          // Feed refresh somwehy fails on amdocs deployment regulairly
          // Fast fix to avoid UI reload
          // TODO: investigate properly
          console.log('failed to refresh feed', err)
          if (err instanceof Unauthorized) {
            throw err
          }
          if (err instanceof Maintenance) {
            throw err
          }
        }
      })
    },
    reset(){
      this.filters.search = ''
      this.filters.favorite = false
      this.filters.category = undefined
      this.filters.date = this.default_date
      this.$refs.filters.clearEntities()
    },
    async clearAll(){
      if (this.clearing_filters) return
      this.clearing_filters = true
      this.$abortDebounce(this.loadItems)
      this.reset()
      await this.$nextTick()
      try {
        await this.loadItems()
      } finally {
        this.clearing_filters = false
      }
    },
    setEntity(id){
      this.reset()
      this.filters.date = 'last_30_days'
      this.$refs.filters.setEntity(id)
    },
    reloadEntities(){
      this.$refs.filters.reloadEntities()
    }
  }
}
